/**
 * Static menu fixtures — Strawberry Garden Signature Menu (39 items, 7 categories).
 * Used when VITE_USE_FIXTURES=true (Cloudflare Pages preview).
 *
 * Image / video files expected under apps/menu-tablet/public/:
 *   /images/items/*.svg       — Aurum-style illustrations (always available)
 *   /images/photos/*.jpg      — AI-generated product photos (user-supplied, per-item)
 *   /videos/teaser.mp4        — Cinematic Straw café teaser (intro on menu open)
 *   /videos/strawberry-parrot.mp4 — Strawberry Parrot drink video
 *
 * If a photo file is missing, the app gracefully falls back to its SVG.
 */

import type { ApiMenuCategory, ApiMenuItem, SommelierRecommendation } from './api';

interface FixtureItem extends ApiMenuItem {
  moodTags: string[];
}

const C = {
  classic:    '11110001-0001-1111-1111-111111111111',
  specialty:  '11110002-0002-1111-1111-111111111111',
  signature:  '11110003-0003-1111-1111-111111111111',
  herbal:     '11110004-0004-1111-1111-111111111111',
  mocktail:   '11110005-0005-1111-1111-111111111111',
  milkshake:  '11110006-0006-1111-1111-111111111111',
  icetea:     '11110007-0007-1111-1111-111111111111',
} as const;

// Per-item photo URLs. These point to the user-supplied JPG names listed in
// docs/photo-mapping.md. Items without a custom photo fall back to their SVG.
const PHOTO = (name: string): string => '/images/photos/' + name + '.jpg';
const SVG   = (name: string): string => '/images/items/'  + name + '.svg';

const ITEMS: FixtureItem[] = [
  // ─── Classic Coffee ────────────────────────────────────────
  { id: 'sg-001', categoryId: C.classic, sku: 'SG-001', nameEn: 'Espresso',       nameAr: 'إسبريسو',
    descriptionEn: 'A pure shot — bold body, lingering crema.',
    descriptionAr: 'جرعة صافية — قوام عميق وكريما تدوم.',
    basePrice: 1.000, currency: 'OMR', prepTimeSeconds: 60,
    imageUrl: SVG('espresso'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'bold', 'classic', 'focused'] },
  { id: 'sg-002', categoryId: C.classic, sku: 'SG-002', nameEn: 'Americano',      nameAr: 'أمريكانو',
    descriptionEn: 'Espresso lengthened with hot water — clean and unhurried.',
    descriptionAr: 'إسبريسو ممدّد بالماء الساخن — نقي وهادئ.',
    basePrice: 1.200, currency: 'OMR', prepTimeSeconds: 90,
    imageUrl: SVG('espresso'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'classic', 'focused'] },
  { id: 'sg-003', categoryId: C.classic, sku: 'SG-003', nameEn: 'Cortado',        nameAr: 'كورتادو',
    descriptionEn: 'Equal parts espresso and warm milk — balanced silk.',
    descriptionAr: 'إسبريسو وحليب دافئ بأجزاء متساوية — حرير متوازن.',
    basePrice: 1.300, currency: 'OMR', prepTimeSeconds: 120,
    imageUrl: SVG('latte'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'classic', 'comforting'] },
  { id: 'sg-004', categoryId: C.classic, sku: 'SG-004', nameEn: 'Flat White',     nameAr: 'فلات وايت',
    descriptionEn: 'Microfoam folded into a double ristretto — velvet finish.',
    descriptionAr: 'رغوة دقيقة تذوب في ريستريتو مزدوج — لمسة مخملية.',
    basePrice: 1.400, currency: 'OMR', prepTimeSeconds: 150,
    imageUrl: SVG('latte'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'classic', 'sophisticated'] },
  { id: 'sg-005', categoryId: C.classic, sku: 'SG-005', nameEn: 'Cappuccino',     nameAr: 'كابتشينو',
    descriptionEn: 'A trio of espresso, steamed milk, and crowned foam.',
    descriptionAr: 'ثلاثية الإسبريسو والحليب المبخّر والرغوة المتوّجة.',
    basePrice: 1.550, currency: 'OMR', prepTimeSeconds: 150,
    imageUrl: SVG('latte'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'classic', 'comforting'] },
  { id: 'sg-006', categoryId: C.classic, sku: 'SG-006', nameEn: 'Latte',          nameAr: 'لاتيه',
    descriptionEn: 'Espresso embraced by silky steamed milk — gentle, generous.',
    descriptionAr: 'إسبريسو يُحتضن بحليب مبخّر حريري — رقيق وسخي.',
    basePrice: 1.850, currency: 'OMR', prepTimeSeconds: 150,
    imageUrl: SVG('latte'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: false,
    moodTags: ['morning', 'classic', 'comforting'] },
  { id: 'sg-007', categoryId: C.classic, sku: 'SG-007', nameEn: 'Spanish Latte',  nameAr: 'لاتيه إسباني',
    descriptionEn: 'Sweetened condensed milk under a velvet espresso pour.',
    descriptionAr: 'حليب محلّى مكثّف تحت تدفّق إسبريسو مخملي.',
    basePrice: 2.100, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('latte'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: true,
    moodTags: ['indulgent', 'sweet', 'afternoon'] },

  // ─── Specialty Coffee ──────────────────────────────────────
  { id: 'sg-101', categoryId: C.specialty, sku: 'SG-101', nameEn: 'V60',          nameAr: 'في 60',
    descriptionEn: 'Hand-poured single origin — clarity in every drop.',
    descriptionAr: 'تقطير يدوي من مصدر واحد — وضوح في كل قطرة.',
    basePrice: 2.100, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('specialty'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: true,
    moodTags: ['contemplative', 'sophisticated', 'morning', 'focused'] },
  { id: 'sg-102', categoryId: C.specialty, sku: 'SG-102', nameEn: 'Chemex',       nameAr: 'كيمكس',
    descriptionEn: 'Slow extraction through thick paper — the cleanest cup we serve.',
    descriptionAr: 'استخلاص بطيء عبر ورق سميك — أنقى كوب نقدّمه.',
    basePrice: 2.100, currency: 'OMR', prepTimeSeconds: 300,
    imageUrl: SVG('specialty'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: true,
    moodTags: ['contemplative', 'sophisticated', 'morning'] },
  { id: 'sg-103', categoryId: C.specialty, sku: 'SG-103', nameEn: 'AeroPress',    nameAr: 'إيرو بريس',
    descriptionEn: 'Pressure and patience — punchy body, low bitterness.',
    descriptionAr: 'ضغط وصبر — قوام نابض وقليل المرارة.',
    basePrice: 2.100, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('specialty'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: false,
    moodTags: ['focused', 'morning', 'bold'] },
  { id: 'sg-104', categoryId: C.specialty, sku: 'SG-104', nameEn: 'Turkish Coffee', nameAr: 'قهوة تركية',
    descriptionEn: 'Finely ground, slow-simmered, served with a date.',
    descriptionAr: 'مطحونة ناعماً، مغليّة على مهل، تُقدَّم مع تمرة.',
    basePrice: 1.300, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('turkish'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: true,
    moodTags: ['arabian', 'ritual', 'evening', 'contemplative'] },
  { id: 'sg-105', categoryId: C.specialty, sku: 'SG-105', nameEn: 'Cold Brew Classic', nameAr: 'كولد برو كلاسيك',
    descriptionEn: 'Steeped overnight — smooth, low-acid, naturally sweet.',
    descriptionAr: 'منقوعة طوال الليل — ناعمة ومنخفضة الحموضة وحلوة طبيعياً.',
    basePrice: 1.200, currency: 'OMR', prepTimeSeconds: 60,
    imageUrl: SVG('coldbrew'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: false,
    moodTags: ['summer', 'refreshing', 'afternoon'] },

  // ─── Signature Coffee ──────────────────────────────────────
  { id: 'sg-201', categoryId: C.signature, sku: 'SG-201', nameEn: 'Orange Seagull', nameAr: 'النورس البرتقالي',
    descriptionEn: 'Orange juice, espresso, whipped cream, and a thread of simple syrup.',
    descriptionAr: 'عصير برتقال، إسبريسو، كريمة مخفوقة، وخيط من السكر الذائب.',
    basePrice: 2.400, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: PHOTO('orange-seagull'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: true,
    moodTags: ['summer', 'refreshing', 'fusion', 'afternoon'] },
  { id: 'sg-202', categoryId: C.signature, sku: 'SG-202', nameEn: 'Strawberry Parrot', nameAr: 'الببغاء الفراولي',
    descriptionEn: 'Fresh strawberry, espresso, whipped cream, simple syrup.',
    descriptionAr: 'فراولة طازجة، إسبريسو، كريمة مخفوقة، سكر ذائب.',
    basePrice: 2.800, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: PHOTO('strawberry-parrot'), videoUrl: '/videos/strawberry-parrot.mp4',
    allergens: ['dairy'], dietaryTags: [], isSignature: true,
    moodTags: ['fruity', 'instagram', 'celebration', 'afternoon'] },
  { id: 'sg-203', categoryId: C.signature, sku: 'SG-203', nameEn: 'Affogato', nameAr: 'أفوغاتو',
    descriptionEn: 'Vanilla bean gelato drowned in a hot espresso pull.',
    descriptionAr: 'جيلاتو الفانيليا يغرق في جرعة إسبريسو ساخنة.',
    basePrice: 2.200, currency: 'OMR', prepTimeSeconds: 120,
    imageUrl: SVG('affogato'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: [], isSignature: true,
    moodTags: ['indulgent', 'evening', 'celebratory', 'instagram'] },
  { id: 'sg-204', categoryId: C.signature, sku: 'SG-204', nameEn: 'STRW Smoke', nameAr: 'دخان الفراولة',
    descriptionEn: 'Cold brew layered with passion fruit and strawberry syrups.',
    descriptionAr: 'كولد برو متطبّق مع شراب الباشن فروت والفراولة.',
    basePrice: 2.200, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('coldbrew'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: true,
    moodTags: ['summer', 'fruity', 'sophisticated', 'afternoon'] },
  { id: 'sg-205', categoryId: C.signature, sku: 'SG-205', nameEn: 'Cold Brew Signature', nameAr: 'كولد برو سيغنتشر',
    descriptionEn: 'Cold brew with ginger ale, lemon, peach, passion fruit and mango.',
    descriptionAr: 'كولد برو مع جنجر إيل، ليمون، خوخ، باشن فروت ومانجو.',
    basePrice: 3.400, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('coldbrew'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: true,
    moodTags: ['summer', 'sophisticated', 'celebratory'] },
  { id: 'sg-206', categoryId: C.signature, sku: 'SG-206', nameEn: 'Spiced Tea', nameAr: 'شاي بالبهارات',
    descriptionEn: 'Black tea simmered with cardamom and cinnamon.',
    descriptionAr: 'شاي أسود مغليّ بالهيل والقرفة.',
    basePrice: 1.800, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('herbal'), videoUrl: null,
    allergens: [], dietaryTags: [], isSignature: false,
    moodTags: ['warm', 'spiced', 'arabian', 'evening'] },

  // ─── Herbal & Garden Teas ─────────────────────────────────
  { id: 'sg-301', categoryId: C.herbal, sku: 'SG-301', nameEn: 'Scarlet Garden', nameAr: 'الحديقة القرمزية',
    descriptionEn: 'Strawberry and orange with mint and rosemary.',
    descriptionAr: 'فراولة وبرتقال مع نعناع وإكليل الجبل.',
    basePrice: 1.700, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: PHOTO('scarlet-garden'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['floral', 'summer', 'refreshing', 'fruity'] },
  { id: 'sg-302', categoryId: C.herbal, sku: 'SG-302', nameEn: 'Golden Orchard', nameAr: 'البستان الذهبي',
    descriptionEn: 'Apple and orange brightened with cinnamon and rosemary.',
    descriptionAr: 'تفاح وبرتقال يتألّقان بالقرفة وإكليل الجبل.',
    basePrice: 1.700, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('herbal'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['warm', 'spiced', 'fruity', 'autumn'] },
  { id: 'sg-303', categoryId: C.herbal, sku: 'SG-303', nameEn: 'Calm Blossom', nameAr: 'سكون الزهر',
    descriptionEn: 'Borage, chamomile, lemon verbena, honey, lemon.',
    descriptionAr: 'لسان الثور، بابونج، لويزة، عسل، ليمون.',
    basePrice: 1.200, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: PHOTO('calm-blossom'), videoUrl: null,
    allergens: [], dietaryTags: ['vegetarian'], isSignature: false,
    moodTags: ['calm', 'evening', 'comforting', 'floral'] },
  { id: 'sg-304', categoryId: C.herbal, sku: 'SG-304', nameEn: 'Blue Citrus Garden', nameAr: 'حديقة الحمضيات الزرقاء',
    descriptionEn: 'Borage, chamomile, lemon verbena, orange zest, mint.',
    descriptionAr: 'لسان الثور، بابونج، لويزة، قشر برتقال، نعناع.',
    basePrice: 1.600, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('herbal'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['calm', 'evening', 'floral', 'bright'] },

  // ─── Cold Mocktails ───────────────────────────────────────
  { id: 'sg-401', categoryId: C.mocktail, sku: 'SG-401', nameEn: 'Strawberry Island', nameAr: 'جزيرة الفراولة',
    descriptionEn: 'Pineapple, strawberry, mint, strawberry & lemon syrups.',
    descriptionAr: 'أناناس، فراولة، نعناع، شراب الفراولة والليمون.',
    basePrice: 3.200, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: PHOTO('strawberry-island'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['summer', 'fruity', 'celebration', 'instagram'] },
  { id: 'sg-402', categoryId: C.mocktail, sku: 'SG-402', nameEn: 'Red Mango Burst', nameAr: 'انفجار المانجو الأحمر',
    descriptionEn: 'Pomegranate, mango, simple syrup, lemon, green apple.',
    descriptionAr: 'رمان، مانجو، سكر ذائب، ليمون، تفاح أخضر.',
    basePrice: 2.600, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('mocktail'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['summer', 'fruity', 'bright'] },
  { id: 'sg-403', categoryId: C.mocktail, sku: 'SG-403', nameEn: 'Berry Peach', nameAr: 'بيري وخوخ',
    descriptionEn: 'Orange juice, peach, lemon, mulberry with peach & lemon syrups.',
    descriptionAr: 'عصير برتقال، خوخ، ليمون، توت أسود مع شراب الخوخ والليمون.',
    basePrice: 3.200, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('mocktail'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['summer', 'fruity', 'instagram'] },
  { id: 'sg-404', categoryId: C.mocktail, sku: 'SG-404', nameEn: 'Mango Berry Bliss', nameAr: 'نعيم المانجو والبيري',
    descriptionEn: 'Mango, banana, strawberry, honey milk.',
    descriptionAr: 'مانجو، موز، فراولة، حليب بالعسل.',
    basePrice: 3.300, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('mocktail'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: ['vegetarian'], isSignature: true,
    moodTags: ['indulgent', 'fruity', 'summer'] },
  { id: 'sg-405', categoryId: C.mocktail, sku: 'SG-405', nameEn: 'Garden Chill', nameAr: 'برودة الحديقة',
    descriptionEn: 'Cucumber, kiwi, mint, lemon, green apple syrup.',
    descriptionAr: 'خيار، كيوي، نعناع، ليمون، شراب التفاح الأخضر.',
    basePrice: 2.600, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: PHOTO('garden-chill'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['refreshing', 'summer', 'bright'] },
  { id: 'sg-406', categoryId: C.mocktail, sku: 'SG-406', nameEn: 'Coco Bloom', nameAr: 'تفتّح جوز الهند',
    descriptionEn: 'Coconut, passion fruit, lavender syrup, coconut water, hibiscus.',
    descriptionAr: 'جوز الهند، باشن فروت، شراب اللافندر، ماء جوز الهند، كركديه.',
    basePrice: 2.400, currency: 'OMR', prepTimeSeconds: 240,
    imageUrl: SVG('mocktail'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['exotic', 'summer', 'floral'] },
  { id: 'sg-407', categoryId: C.mocktail, sku: 'SG-407', nameEn: 'Red Mirage', nameAr: 'السراب الأحمر',
    descriptionEn: 'Pomegranate, orange, lemon, soda, simple syrup.',
    descriptionAr: 'رمان، برتقال، ليمون، صودا، سكر ذائب.',
    basePrice: 2.800, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('mocktail'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['refreshing', 'summer', 'bright'] },
  { id: 'sg-408', categoryId: C.mocktail, sku: 'SG-408', nameEn: 'Sunset Fizz', nameAr: 'فقاعات الغروب',
    descriptionEn: 'Strawberry, mango, pineapple, lemon juice, soda.',
    descriptionAr: 'فراولة، مانجو، أناناس، عصير ليمون، صودا.',
    basePrice: 2.400, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: PHOTO('sunset-fizz'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['summer', 'fruity', 'celebration'] },

  // ─── Milkshakes ───────────────────────────────────────────
  { id: 'sg-501', categoryId: C.milkshake, sku: 'SG-501', nameEn: 'Strawberry Shake', nameAr: 'ميلك شيك الفراولة',
    descriptionEn: 'Ice cream, milk, fresh strawberry, syrup and cream.',
    descriptionAr: 'آيس كريم، حليب، فراولة طازجة، شراب وكريمة.',
    basePrice: 2.500, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('milkshake'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: ['vegetarian'], isSignature: false,
    moodTags: ['indulgent', 'summer', 'celebration'] },
  { id: 'sg-502', categoryId: C.milkshake, sku: 'SG-502', nameEn: 'Chocolate Shake', nameAr: 'ميلك شيك الشوكولاتة',
    descriptionEn: 'Ice cream, milk, chocolate chips, cocoa, Nutella, cream.',
    descriptionAr: 'آيس كريم، حليب، رقائق شوكولاتة، كاكاو، نوتيلا، كريمة.',
    basePrice: 3.800, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('milkshake'), videoUrl: null,
    allergens: ['dairy', 'nuts'], dietaryTags: ['vegetarian'], isSignature: true,
    moodTags: ['indulgent', 'celebration', 'instagram'] },
  { id: 'sg-503', categoryId: C.milkshake, sku: 'SG-503', nameEn: 'Berry Shake', nameAr: 'ميلك شيك التوت',
    descriptionEn: 'Ice cream, milk, strawberry, blueberry, mulberry, raspberry, cream.',
    descriptionAr: 'آيس كريم، حليب، فراولة، توت أزرق، توت أسود، توت العليق، كريمة.',
    basePrice: 4.500, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: PHOTO('berry-shake'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: ['vegetarian'], isSignature: true,
    moodTags: ['indulgent', 'celebration', 'fruity'] },
  { id: 'sg-504', categoryId: C.milkshake, sku: 'SG-504', nameEn: 'Tropical Shake', nameAr: 'ميلك شيك الاستوائي',
    descriptionEn: 'Ice cream, milk, mango, pineapple, banana, cream.',
    descriptionAr: 'آيس كريم، حليب، مانجو، أناناس، موز، كريمة.',
    basePrice: 2.600, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('milkshake'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: ['vegetarian'], isSignature: false,
    moodTags: ['summer', 'fruity', 'exotic'] },
  { id: 'sg-505', categoryId: C.milkshake, sku: 'SG-505', nameEn: 'Nutella Berry Shake', nameAr: 'ميلك شيك النوتيلا والتوت',
    descriptionEn: 'Ice cream, milk, strawberry, Nutella, cream.',
    descriptionAr: 'آيس كريم، حليب، فراولة، نوتيلا، كريمة.',
    basePrice: 4.000, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('milkshake'), videoUrl: null,
    allergens: ['dairy', 'nuts'], dietaryTags: ['vegetarian'], isSignature: true,
    moodTags: ['indulgent', 'celebration', 'instagram'] },

  // ─── Iced Teas ────────────────────────────────────────────
  { id: 'sg-601', categoryId: C.icetea, sku: 'SG-601', nameEn: 'Peach Splash', nameAr: 'رشّة الخوخ',
    descriptionEn: 'Peach syrup, lemon syrup and lemon juice over chilled tea.',
    descriptionAr: 'شراب الخوخ والليمون مع عصير ليمون فوق شاي مثلج.',
    basePrice: 1.400, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('icetea'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['summer', 'fruity', 'refreshing'] },
  { id: 'sg-602', categoryId: C.icetea, sku: 'SG-602', nameEn: 'Ruby Breeze', nameAr: 'نسيم الياقوت',
    descriptionEn: 'Strawberry & lemon syrups with lemon juice over iced tea.',
    descriptionAr: 'شراب الفراولة والليمون مع عصير ليمون فوق شاي مثلج.',
    basePrice: 1.400, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('icetea'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: false,
    moodTags: ['summer', 'fruity', 'refreshing'] },
  { id: 'sg-603', categoryId: C.icetea, sku: 'SG-603', nameEn: 'Golden Salted Mango', nameAr: 'مانجو ذهبي مملّح',
    descriptionEn: 'Mango, caramel, cream, simple syrup, salt over iced tea.',
    descriptionAr: 'مانجو، كراميل، كريمة، سكر ذائب، ملح فوق شاي مثلج.',
    basePrice: 1.800, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('icetea'), videoUrl: null,
    allergens: ['dairy'], dietaryTags: ['vegetarian'], isSignature: true,
    moodTags: ['indulgent', 'summer', 'sophisticated'] },
  { id: 'sg-604', categoryId: C.icetea, sku: 'SG-604', nameEn: 'Bloody Dream', nameAr: 'الحلم الدامي',
    descriptionEn: 'Hibiscus tea, lavender, strawberry, lemon, peach syrups, rose water.',
    descriptionAr: 'شاي الكركديه، لافندر، فراولة، ليمون، شراب الخوخ، ماء ورد.',
    basePrice: 1.200, currency: 'OMR', prepTimeSeconds: 180,
    imageUrl: SVG('icetea'), videoUrl: null,
    allergens: [], dietaryTags: ['vegan'], isSignature: true,
    moodTags: ['floral', 'evening', 'sophisticated'] },
];

export const FIXTURE_MENU: ApiMenuCategory[] = [
  { id: C.classic,    nameEn: 'Classic Coffee',     nameAr: 'القهوة الكلاسيكية',  slug: 'classic-coffee',     sortOrder: 1, items: ITEMS.filter((x) => x.categoryId === C.classic) },
  { id: C.specialty,  nameEn: 'Specialty Coffee',   nameAr: 'قهوة المختصة',       slug: 'specialty-coffee',   sortOrder: 2, items: ITEMS.filter((x) => x.categoryId === C.specialty) },
  { id: C.signature,  nameEn: 'Signature Coffee',   nameAr: 'القهوة المميّزة',    slug: 'signature-coffee',   sortOrder: 3, items: ITEMS.filter((x) => x.categoryId === C.signature) },
  { id: C.herbal,     nameEn: 'Herbal Garden',      nameAr: 'أعشاب الحديقة',     slug: 'herbal-garden',      sortOrder: 4, items: ITEMS.filter((x) => x.categoryId === C.herbal) },
  { id: C.mocktail,   nameEn: 'Cold Mocktails',     nameAr: 'موكتيلات باردة',     slug: 'cold-mocktails',     sortOrder: 5, items: ITEMS.filter((x) => x.categoryId === C.mocktail) },
  { id: C.milkshake,  nameEn: 'Milkshakes',         nameAr: 'ميلك شيك',           slug: 'milkshakes',         sortOrder: 6, items: ITEMS.filter((x) => x.categoryId === C.milkshake) },
  { id: C.icetea,     nameEn: 'Iced Teas',          nameAr: 'الشاي المثلّج',      slug: 'iced-teas',          sortOrder: 7, items: ITEMS.filter((x) => x.categoryId === C.icetea) },
];

export function fixtureSommelier(mood: string): SommelierRecommendation[] {
  const tokens = mood.toLowerCase().split(/[\s,]+/).filter((t) => t.length > 1);
  if (tokens.length === 0) return [];
  const scored = ITEMS.map((it) => {
    const haystack = [
      ...it.moodTags,
      it.nameEn.toLowerCase(),
      it.descriptionEn?.toLowerCase() ?? '',
    ].join(' ');
    let score = 0;
    for (const t of tokens) if (haystack.includes(t)) score += 1;
    return { it, score: score / tokens.length };
  });
  return scored
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map(({ it, score }) => ({
      itemId: it.id,
      sku: it.sku,
      nameAr: it.nameAr,
      nameEn: it.nameEn,
      basePrice: it.basePrice,
      currency: it.currency,
      score,
      reason: it.descriptionEn?.slice(0, 200) ?? '',
    }));
}
